// NEC 2023 Table 240.6(A)
// Standard Ampere Ratings for Fuses and Inverse Time Circuit Breakers

export const STANDARD_BREAKER_SIZES: number[] = [
  15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100,
  110, 125, 150, 175, 200, 225, 250, 300, 350, 400, 450,
  500, 600, 700, 800, 1000, 1200, 1600, 2000, 2500, 3000,
  4000, 5000, 6000
];

/** Largest standard rating in Table 240.6(A). */
export const MAX_STANDARD_BREAKER = STANDARD_BREAKER_SIZES[STANDARD_BREAKER_SIZES.length - 1];

/**
 * True when no standard rating in Table 240.6(A) is large enough. Callers
 * must not present roundToStandardBreaker()'s capped result as a valid device.
 */
export function exceedsStandardBreakerSizes(amps: number): boolean {
  return amps > MAX_STANDARD_BREAKER;
}

/**
 * Round up to the next standard breaker size, or null when the required
 * amperage exceeds the largest standard rating.
 */
export function roundToStandardBreakerOrNull(amps: number): number | null {
  return exceedsStandardBreakerSizes(amps) ? null : roundToStandardBreaker(amps);
}

/**
 * Round up to the next standard breaker size
 * @param amps - Required amperage
 * @returns Next standard breaker size >= amps (capped at 6000 A; check
 *          exceedsStandardBreakerSizes() before trusting the cap)
 */
export function roundToStandardBreaker(amps: number): number {
  for (const size of STANDARD_BREAKER_SIZES) {
    if (size >= amps) {
      return size;
    }
  }
  // If larger than any standard size, return the largest
  return STANDARD_BREAKER_SIZES[STANDARD_BREAKER_SIZES.length - 1];
}

/**
 * Get the next size up from a given standard breaker size
 * @param currentSize - Current standard breaker size
 * @returns Next larger standard size, or current if already at max
 */
export function getNextBreakerSize(currentSize: number): number {
  const index = STANDARD_BREAKER_SIZES.indexOf(currentSize);
  if (index === -1) {
    // Not a standard size, round up
    return roundToStandardBreaker(currentSize);
  }
  if (index === STANDARD_BREAKER_SIZES.length - 1) {
    return currentSize; // Already at max
  }
  return STANDARD_BREAKER_SIZES[index + 1];
}

/**
 * Check if a value is a standard breaker size
 * @param amps - Value to check
 * @returns True if it's a standard size
 */
export function isStandardBreakerSize(amps: number): boolean {
  return STANDARD_BREAKER_SIZES.includes(amps);
}

// Standard fuse sizes include smaller ratings not available as circuit breakers
export const STANDARD_FUSE_SIZES: number[] = [
  1, 3, 6, 10,
  ...STANDARD_BREAKER_SIZES,
];

/**
 * Round up to the next standard fuse size
 * @param amps - Required amperage
 * @returns Next standard fuse size >= amps
 */
export function roundToStandardFuse(amps: number): number {
  for (const size of STANDARD_FUSE_SIZES) {
    if (size >= amps) {
      return size;
    }
  }
  return STANDARD_FUSE_SIZES[STANDARD_FUSE_SIZES.length - 1];
}

/**
 * Round down to the next lower standard breaker size
 * @param amps - Maximum amperage
 * @returns Largest standard breaker size <= amps
 */
export function roundDownToStandardBreaker(amps: number): number {
  let result = STANDARD_BREAKER_SIZES[0];
  for (const size of STANDARD_BREAKER_SIZES) {
    if (size <= amps) {
      result = size;
    } else {
      break;
    }
  }
  return result;
}

/**
 * Round down to the next lower standard fuse size
 * @param amps - Maximum amperage
 * @returns Largest standard fuse size <= amps
 */
export function roundDownToStandardFuse(amps: number): number {
  let result = STANDARD_FUSE_SIZES[0];
  for (const size of STANDARD_FUSE_SIZES) {
    if (size <= amps) {
      result = size;
    } else {
      break;
    }
  }
  return result;
}

export const NEC_TABLE_240_6A_REF = {
  table: 'Table 240.6(A)',
  article: '240.6(A)',
  description: 'Standard Ampere Ratings for Fuses and Inverse Time Circuit Breakers',
};
